import {
  AnalysisSettings,
  CreatePresetRequest,
  FeaturePatternTrussSettings,
  LoadSettings,
  MaterialSelection,
  MaterialSettings,
  OrganizationPreset,
  ProfileDefaults,
  UpdatePresetRequest,
} from '@paragon/contract-library';

import { DesignService } from '../connections';

// ==================== GET /{settingsType} ====================

export function getLoadSettingsPresets(): Promise<OrganizationPreset<LoadSettings>[]> {
  return DesignService.get('presets/loadSettings');
}

export function getAnalysisSettingsPresets(): Promise<OrganizationPreset<AnalysisSettings>[]> {
  return DesignService.get('presets/analysisSettings');
}

export function getMaterialSettingsPresets(): Promise<OrganizationPreset<MaterialSettings>[]> {
  return DesignService.get('presets/materialSettings');
}

export function getFeaturePatternTrussSettingsPresets(): Promise<OrganizationPreset<FeaturePatternTrussSettings>[]> {
  return DesignService.get('presets/featurePatternTrussSettings');
}

export function getProfileDefaultsPresets(): Promise<OrganizationPreset<ProfileDefaults>[]> {
  return DesignService.get('presets/profileDefaults');
}

// ==================== GET /{settingsType}/{presetGuid} ====================

export function getLoadSettingsPreset(presetGuid: string): Promise<OrganizationPreset<LoadSettings>> {
  return DesignService.get(`presets/loadSettings/${presetGuid}`);
}

export function getAnalysisSettingsPreset(presetGuid: string): Promise<OrganizationPreset<AnalysisSettings>> {
  return DesignService.get(`presets/analysisSettings/${presetGuid}`);
}

export function getMaterialSettingsPreset(presetGuid: string): Promise<OrganizationPreset<MaterialSettings>> {
  return DesignService.get(`presets/materialSettings/${presetGuid}`);
}

export function getFeaturePatternTrussSettingsPreset(
  presetGuid: string,
): Promise<OrganizationPreset<FeaturePatternTrussSettings>> {
  return DesignService.get(`presets/featurePatternTrussSettings/${presetGuid}`);
}

export function getProfileDefaultsPreset(presetGuid: string): Promise<OrganizationPreset<ProfileDefaults>> {
  return DesignService.get(`presets/profileDefaults/${presetGuid}`);
}

// ==================== POST /{settingsType} ====================

export function createLoadSettingsPreset(
  presetName: string,
  settings: LoadSettings,
  materialSelection?: MaterialSelection,
): Promise<OrganizationPreset<LoadSettings>> {
  if (materialSelection != null) {
    throw new Error('A material selection should not be provided when creating a load settings preset.');
  }

  return DesignService.post('presets/loadSettings', {
    data: { presetName, settings } satisfies CreatePresetRequest<LoadSettings>,
  });
}

export function createAnalysisSettingsPreset(
  presetName: string,
  settings: AnalysisSettings,
  materialSelection?: MaterialSelection,
): Promise<OrganizationPreset<AnalysisSettings>> {
  if (materialSelection != null) {
    throw new Error('A material selection should not be provided when creating an analysis settings preset.');
  }

  return DesignService.post('presets/analysisSettings', {
    data: { presetName, settings } satisfies CreatePresetRequest<AnalysisSettings>,
  });
}

export function createMaterialSettingsPreset(
  presetName: string,
  settings: MaterialSettings,
  materialSelection?: MaterialSelection,
): Promise<OrganizationPreset<MaterialSettings>> {
  if (materialSelection == null) {
    throw new Error('A material selection is required when creating a material settings preset.');
  }

  return DesignService.post('presets/materialSettings', {
    data: { presetName, settings, materialSelection } satisfies CreatePresetRequest<MaterialSettings>,
  });
}

export function createFeaturePatternTrussSettingsPreset(
  presetName: string,
  settings: FeaturePatternTrussSettings,
  materialSelection?: MaterialSelection,
): Promise<OrganizationPreset<FeaturePatternTrussSettings>> {
  if (materialSelection != null) {
    throw new Error(
      'A material selection should not be provided when creating a feature pattern truss settings preset.',
    );
  }

  return DesignService.post('presets/featurePatternTrussSettings', {
    data: { presetName, settings } satisfies CreatePresetRequest<FeaturePatternTrussSettings>,
  });
}

export function createProfileDefaultsPreset(
  presetName: string,
  settings: ProfileDefaults,
  materialSelection?: MaterialSelection,
): Promise<OrganizationPreset<ProfileDefaults>> {
  if (materialSelection != null) {
    throw new Error('A material selection should not be provided when creating a profile defaults preset.');
  }

  return DesignService.post('presets/profileDefaults', {
    data: { presetName, settings } satisfies CreatePresetRequest<ProfileDefaults>,
  });
}

// ==================== PUT /{settingsType}/{presetGuid} ====================

export function updateLoadSettingsPreset(
  presetGuid: string,
  presetName: string,
  settings: LoadSettings,
  materialSelection?: MaterialSelection,
): Promise<OrganizationPreset<LoadSettings>> {
  if (materialSelection != null) {
    throw new Error('A material selection should not be provided when updating a load settings preset.');
  }

  return DesignService.put(`presets/loadSettings/${presetGuid}`, {
    data: { presetName, settings } satisfies UpdatePresetRequest<LoadSettings>,
  });
}

export function updateAnalysisSettingsPreset(
  presetGuid: string,
  presetName: string,
  settings: AnalysisSettings,
  materialSelection?: MaterialSelection,
): Promise<OrganizationPreset<AnalysisSettings>> {
  if (materialSelection != null) {
    throw new Error('A material selection should not be provided when updating an analysis settings preset.');
  }

  return DesignService.put(`presets/analysisSettings/${presetGuid}`, {
    data: { presetName, settings } satisfies UpdatePresetRequest<AnalysisSettings>,
  });
}

export function updateMaterialSettingsPreset(
  presetGuid: string,
  presetName: string,
  settings: MaterialSettings,
  materialSelection?: MaterialSelection,
): Promise<OrganizationPreset<MaterialSettings>> {
  if (materialSelection == null) {
    throw new Error('A material selection is required when updating a material settings preset.');
  }

  return DesignService.put(`presets/materialSettings/${presetGuid}`, {
    data: { presetName, settings, materialSelection } satisfies UpdatePresetRequest<MaterialSettings>,
  });
}

export function updateFeaturePatternTrussSettingsPreset(
  presetGuid: string,
  presetName: string,
  settings: FeaturePatternTrussSettings,
  materialSelection?: MaterialSelection,
): Promise<OrganizationPreset<FeaturePatternTrussSettings>> {
  if (materialSelection != null) {
    throw new Error(
      'A material selection should not be provided when updating a feature pattern truss settings preset.',
    );
  }

  return DesignService.put(`presets/featurePatternTrussSettings/${presetGuid}`, {
    data: { presetName, settings } satisfies UpdatePresetRequest<FeaturePatternTrussSettings>,
  });
}

export function updateProfileDefaultsPreset(
  presetGuid: string,
  presetName: string,
  settings: ProfileDefaults,
  materialSelection?: MaterialSelection,
): Promise<OrganizationPreset<ProfileDefaults>> {
  if (materialSelection != null) {
    throw new Error('A material selection should not be provided when updating a profile defaults preset.');
  }

  return DesignService.put(`presets/profileDefaults/${presetGuid}`, {
    data: { presetName, settings } satisfies UpdatePresetRequest<ProfileDefaults>,
  });
}

// ==================== DELETE /{presetGuid} ====================

export function deletePreset(presetGuid: string): Promise<void> {
  return DesignService.delete(`presets/${presetGuid}`);
}
